import { useScreenerBuilderEnabled } from "@/hooks/screener-builder-hooks";
import { faClockRotateLeft } from "@fortawesome/pro-solid-svg-icons/faClockRotateLeft";
import { faEdit } from "@fortawesome/pro-solid-svg-icons/faEdit";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { LoadingOverlay } from "@mantine/core";
import { type CellContext, flexRender } from "@tanstack/react-table";
import { useRouter } from "next/navigation";
import { useCallback, useMemo } from "react";
import { twMerge } from "tailwind-merge";

import {
  ARCHIVED_AT,
  type ArchivedScreenersTableQueryParams,
  formatScreenerColumns,
  getScreenerPinningStyles,
  SCREENERS_DEFAULT_SORT,
  ScreenersFilterKeys,
  ScreenersQueryKeys,
  type ScreenersTableQueryParams,
  ScreenersTableSortableColumns,
} from "./screeners-columns";
import { getAllScreeners } from "@/app/api/screeners";
import {
  CheckboxFilter,
  convertStringArrayToCheckboxOption,
  CreatedByFilter,
  FilterGroup,
  FilterMenu,
} from "@/components/filters";
import {
  TableEmptyState,
  tableEmptyStateClearFiltersProps,
} from "@/components/table/views/table-empty-state";
import { TableSharedWrapper } from "@/components/table/views/table-shared-wrapper";
import { useUserContext } from "@/context/user.context";
import { useTrackAnalytics } from "@/hooks/analytics.hooks";
import {
  useAllScreenersQuery,
  useRestoreArchivedScreenerMutation,
} from "@/hooks/queries/screener.queries";
import {
  useTableQueryWithRecovery,
  cleanScreenersFilters,
} from "@/hooks/queries/use-table-query-with-recovery";
import {
  useDebouncedFilterParams,
  useUrlSyncedFilterParams,
} from "@/hooks/use-filter-params";
import { useServerPaginatedTable } from "@/hooks/use-server-paginated-table";
import type { Screener } from "@/types/screeners";
import { TestID } from "@/utils/helpers";
import {
  ALL_SCREENER_PREFIX,
  ARCHIVED_SCREENER_PREFIX,
} from "@/utils/local-storage";
import {
  NotificationTypes,
  showUpwageNotification,
} from "@/utils/notifications";

interface ScreenersTableProps {
  isArchived?: boolean;
}

export const ScreenersTable = ({ isArchived = false }: ScreenersTableProps) => {
  const router = useRouter();
  const track = useTrackAnalytics();
  const { user } = useUserContext();
  const screenerBuilderEnabled = useScreenerBuilderEnabled();

  const localStoragePrefix = useMemo(
    () => (isArchived ? ARCHIVED_SCREENER_PREFIX : ALL_SCREENER_PREFIX),
    [isArchived],
  );
  const { handle: filterParams, initialized } = useUrlSyncedFilterParams<
    ScreenersTableQueryParams | ArchivedScreenersTableQueryParams
  >(localStoragePrefix, {
    ...SCREENERS_DEFAULT_SORT,
    ...(isArchived ? { getArchived: true as const } : {}),
  });
  const [debouncedFilterParams, isDebouncing] = useDebouncedFilterParams(
    filterParams,
    300,
  );
  const { mutate: restoreArchivedScreener, isPending: restoreMutationPending } =
    useRestoreArchivedScreenerMutation();

  const baseQuery = useAllScreenersQuery(debouncedFilterParams, {
    enabled: initialized,
  });

  const getDefaultParamsString = useCallback(
    () =>
      new URLSearchParams({
        ...SCREENERS_DEFAULT_SORT,
        ...(isArchived ? { getArchived: "true" } : {}),
      }).toString(),
    [isArchived],
  );

  const fetchWithoutParams = useCallback(
    () => getAllScreeners(getDefaultParamsString()),
    [getDefaultParamsString],
  );

  const {
    data: tableDataResponse,
    isFetched,
    isFetching,
    isLoading,
  } = useTableQueryWithRecovery({
    cleanStaleFilters: cleanScreenersFilters,
    fetchWithoutParams,
    filterParams,
    query: baseQuery,
  });
  const {
    jobTitles,
    locations,
    statuses,
    totalItems,
    totalPages,
    types,
    users,
  } = useMemo(
    () => ({
      jobTitles: tableDataResponse?.availableFilters.jobTitles ?? [],
      locations: tableDataResponse?.availableFilters.locations ?? [],
      statuses: tableDataResponse?.availableFilters.statuses ?? [],
      totalItems: tableDataResponse?.pagination.totalItems ?? 0,
      totalPages: tableDataResponse?.pagination.totalPages ?? 0,
      types: tableDataResponse?.availableFilters.types ?? [],
      users: tableDataResponse?.availableFilters.users ?? [],
    }),
    [tableDataResponse],
  );
  const onRestoreClick = useCallback(
    (info: CellContext<Screener, void>) => {
      const screenerId = info.row.original.id;

      track("Restore Screener Clicked", { screener_id: screenerId });
      restoreArchivedScreener(
        { screenerId },
        {
          onError: () =>
            showUpwageNotification(
              "There was a problem communicating with the server",
              NotificationTypes.Error,
            ),
          onSuccess: () =>
            showUpwageNotification(
              "Screener restored",
              NotificationTypes.Success,
            ),
        },
      );
    },
    [restoreArchivedScreener, track],
  );
  const columns = useMemo(
    () =>
      formatScreenerColumns(
        tableDataResponse?.columns ?? [],
        isArchived,
        isArchived
          ? {
              children: "Restore",
              onClick: onRestoreClick,
              rightSection: <FontAwesomeIcon icon={faClockRotateLeft} />,
              testId: new TestID("archived-screeners", "table", "restore"),
            }
          : {
              buildHref: (info) =>
                `/screener-builder/${info.row.original.id}/locations`,
              children: screenerBuilderEnabled ? "Edit" : "Details",
              onClick: () => track("Screener Table Edit Clicked"),
              rightSection: screenerBuilderEnabled ? (
                <FontAwesomeIcon icon={faEdit} />
              ) : null,
              testId: new TestID("active-screeners", "table", "edit"),
            },
      ),
    [
      isArchived,
      onRestoreClick,
      screenerBuilderEnabled,
      tableDataResponse?.columns,
      track,
    ],
  );
  const tableData = useMemo(
    () => tableDataResponse?.screenerRows ?? [],
    [tableDataResponse],
  );

  const { loadingFallback, table } = useServerPaginatedTable({
    columns,
    columnPinning: {
      left: [ScreenersQueryKeys.jobTitle],
      right: [],
    },
    columnVisibility: {
      // Users without edit permissions cannot restore archived screeners
      actions: screenerBuilderEnabled || !isArchived,
      userId: false,
      type: Boolean(user?.isInternal),
    },
    data: tableData,
    isLoading,
    totalPages,
  });

  const hasClearableFilters = filterParams.hasAnyOf(ScreenersFilterKeys);

  // Only truthy during loading
  if (loadingFallback) {
    return loadingFallback;
  }

  return (
    <TableSharedWrapper
      emptyStateUI={
        <TableEmptyState
          {...tableEmptyStateClearFiltersProps(
            "screeners",
            hasClearableFilters,
            filterParams.clear,
          )}
        />
      }
      filterParams={filterParams}
      filters={
        <FilterMenu<ScreenersTableQueryParams>
          clearAllFilters={filterParams.clear}
          hasClearableFilters={hasClearableFilters}
          filters={filterParams}
          rowCount={totalItems}
          searchCountName={`${isArchived ? "archived " : ""}interviewers`}
        >
          <LoadingOverlay visible={restoreMutationPending} />
          {!isArchived && (
            <FilterGroup title="Status">
              <CheckboxFilter<"status", ScreenersTableQueryParams>
                analyticsName={"Screener status"}
                key={ScreenersQueryKeys.status}
                name={ScreenersQueryKeys.status}
                options={convertStringArrayToCheckboxOption(statuses)}
                filters={filterParams}
              />
            </FilterGroup>
          )}
          <FilterGroup title="Location">
            <CheckboxFilter<"location", ScreenersTableQueryParams>
              analyticsName={"Location"}
              key={ScreenersQueryKeys.location}
              name={ScreenersQueryKeys.location}
              options={convertStringArrayToCheckboxOption(locations)}
              filters={filterParams}
            />
          </FilterGroup>
          <FilterGroup title="Job Title">
            <CheckboxFilter<"jobTitle", ScreenersTableQueryParams>
              analyticsName={"Job Title"}
              key={ScreenersQueryKeys.jobTitle}
              name={ScreenersQueryKeys.jobTitle}
              options={convertStringArrayToCheckboxOption(jobTitles)}
              filters={filterParams}
            />
          </FilterGroup>
          <FilterGroup title="Created By">
            <CreatedByFilter filters={filterParams} users={users} />
          </FilterGroup>
          {user?.isInternal && types.length > 0 && (
            <FilterGroup title="Type">
              <CheckboxFilter<"type", ScreenersTableQueryParams>
                analyticsName={"Screener type"}
                key={ScreenersQueryKeys.type}
                name={ScreenersQueryKeys.type}
                options={convertStringArrayToCheckboxOption(types)}
                filters={filterParams}
              />
            </FilterGroup>
          )}
        </FilterMenu>
      }
      getPinningStyles={getScreenerPinningStyles}
      isFetched={isFetched}
      isLoading={isFetching || isDebouncing}
      page={filterParams.getWithDefault(ScreenersQueryKeys.page, 1)}
      setPage={(page) => filterParams.set(ScreenersQueryKeys.page, page)}
      sortableColumns={
        isArchived
          ? [...Object.values(ScreenersTableSortableColumns), ARCHIVED_AT]
          : Object.values(ScreenersTableSortableColumns)
      }
      table={table}
      totalPages={totalPages}
    >
      {table.getRowModel().rows.map((row) => (
        <tr
          key={row.id}
          className={twMerge(
            "border-b",
            "border-uw-gray-2",
            !isArchived && ["cursor-pointer", "hover:brightness-95"],
          )}
          data-testid={row.original.id}
          onClick={() => {
            if (!isArchived) {
              const { id, isDraft } = row.original;

              track(`Screener${isDraft ? " Draft" : ""} Clicked`, {
                screener_id: id,
              });

              router.push(`/screener${isDraft ? "-builder" : ""}/${id}`);
            }
          }}
        >
          {row.getVisibleCells().map((cell) => (
            <td
              key={cell.id}
              className="p-3 w-max text-sm"
              style={{
                ...getScreenerPinningStyles(cell.column, row),
              }}
            >
              {flexRender(cell.column.columnDef.cell, cell.getContext())}
            </td>
          ))}
        </tr>
      ))}
    </TableSharedWrapper>
  );
};
